import type { AuthUser, CaseDetail, CaseSummary, NewsList, NewsPost, NewsSummary, ReportDraft, Scope, TonightSky } from './types';

export const API_BASE = import.meta.env.VITE_API_URL ?? 'https://strange-skies-api.shrowebdesign.workers.dev';
const SESSION_KEY = 'strange-skies-web-session';

export class ApiError extends Error {
  constructor(public status: number | null, message: string) { super(message); }
}

export const session = {
  get: () => window.localStorage.getItem(SESSION_KEY),
  set: (token: string) => window.localStorage.setItem(SESSION_KEY, token),
  clear: () => window.localStorage.removeItem(SESSION_KEY),
};

async function request<T>(path: string, init: RequestInit = {}, timeoutMs = 20_000): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  const token = session.get();
  const isFormData = init.body instanceof FormData;
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        ...(isFormData ? {} : { 'content-type': 'application/json' }),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(init.headers ?? {}),
      },
    });
    const data = await response.json().catch(() => ({})) as { message?: string };
    if (!response.ok) throw new ApiError(response.status, data.message ?? `Request failed (${response.status}).`);
    return data as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (error instanceof Error && error.name === 'AbortError') throw new ApiError(null, 'The request timed out.');
    throw new ApiError(null, 'The case network could not be reached.');
  } finally { window.clearTimeout(timer); }
}

export async function fetchCases(options: { scope?: Scope; latitude?: number; longitude?: number; countryCode?: string } = {}) {
  const query = new URLSearchParams({ limit: '100' });
  if (options.scope === 'national' && options.countryCode) query.set('country', options.countryCode);
  if ((options.scope === 'local' || options.scope === 'regional') && options.latitude !== undefined && options.longitude !== undefined) {
    query.set('lat', String(options.latitude));
    query.set('lng', String(options.longitude));
    query.set('radius_km', options.scope === 'local' ? '30' : '150');
  }
  return (await request<{ reports: CaseSummary[] }>(`/v1/reports?${query}`)).reports;
}

export async function fetchCase(caseNumber: string) {
  try { return (await request<{ case: CaseDetail }>(`/v1/reports/${encodeURIComponent(caseNumber)}`)).case; }
  catch (error) { if (error instanceof ApiError && error.status === 404) return null; throw error; }
}

export async function fetchUserReports(username: string) {
  return (await request<{ reports: CaseSummary[] }>(`/v1/users/${encodeURIComponent(username)}/reports`)).reports;
}

export async function authenticate(mode: 'login' | 'register', username: string, password: string) {
  return request<{ token: string; user: AuthUser }>(`/v1/auth/${mode}`, { method: 'POST', body: JSON.stringify({ username, password }) });
}
export async function fetchMe() { return (await request<{ user: AuthUser }>('/v1/auth/me')).user; }
export async function generateRecoveryCode(password: string) { return request<{ code: string }>('/v1/auth/recovery-code', { method: 'POST', body: JSON.stringify({ password }) }); }
export async function recoverAccount(username: string, code: string, password: string) { return request('/v1/auth/recover', { method: 'POST', body: JSON.stringify({ username, code, password }) }); }
export async function logout() { await request('/v1/auth/logout', { method: 'POST', body: '{}' }); }
export async function addComment(reportId: string, body: string) { return request<{ id: string }>(`/v1/reports/${reportId}/comments`, { method: 'POST', body: JSON.stringify({ body }) }); }
export async function corroborateCase(reportId: string, description?: string) { return request<{ id: string }>(`/v1/reports/${reportId}/corroborate`, { method: 'POST', body: JSON.stringify({ occurredAt: new Date().toISOString(), description }) }); }

export async function submitReport(draft: ReportDraft, files: File[], idempotencyKey: string) {
  const result = await request<{ id: string; case_number: string; status: string }>('/v1/reports', {
    method: 'POST',
    body: JSON.stringify({
      idempotencyKey, occurredAt: new Date(draft.occurredAt).toISOString(),
      location: { latitude: draft.latitude, longitude: draft.longitude }, locationName: draft.locationName || undefined,
      countryCode: draft.countryCode || undefined, description: draft.description, objectCount: draft.objectCount,
      shape: draft.shape || undefined, colour: draft.colour || undefined, movementType: draft.movementType || undefined,
      movementDirection: draft.movementDirection || undefined, durationSeconds: draft.durationSeconds,
      sound: draft.sound || undefined, flashing: draft.flashing,
    }),
  });
  const failedFiles = await uploadEvidence(result.id, files);
  return { ...result, failedFiles };
}

export async function uploadEvidence(reportId: string, files: File[]): Promise<string[]> {
  if (files.length > 10 || files.some((file) => file.size > 100 * 1024 * 1024)) throw new ApiError(422, 'Choose up to 10 files, each 100 MB or smaller.');
  const failedFiles: string[] = [];
  for (const file of files) {
    const form = new FormData();
    form.append('type', file.type.startsWith('video/') ? 'video' : 'photo');
    form.append('file', file);
    try { await request(`/v1/reports/${reportId}/media-upload`, { method: 'POST', body: form }, 120_000); }
    catch { failedFiles.push(file.name); }
  }
  return failedFiles;
}

export interface NearbyCase { case_number: string; status: string; classification: string | null; distance_km: number; occurred_at: string; latitude: number; longitude: number; location_name: string | null; shape: string | null; colour: string | null }
export async function fetchNearby(latitude: number, longitude: number, radiusKm = 150) {
  return (await request<{ reports: NearbyCase[] }>(`/v1/nearby?lat=${latitude}&lng=${longitude}&radius_km=${radiusKm}`)).reports;
}

export async function subscribeAlerts(input: { email: string; latitude: number; longitude: number; radiusKm: number; placeName?: string }) {
  await request('/v1/alerts', { method: 'POST', body: JSON.stringify(input) });
}
export async function confirmAlerts(token: string) {
  return request<{ ok: boolean; radius_km: number; place_name: string | null }>(`/v1/alerts/confirm?token=${encodeURIComponent(token)}`);
}
export async function unsubscribeAlerts(token: string) {
  await request('/v1/alerts/unsubscribe', { method: 'POST', body: JSON.stringify({ token }) });
}

export interface TleRecord { name: string; line1: string; line2: string; group: string }
export async function fetchSatelliteElements() {
  return (await request<{ fetched_at: string; satellites: TleRecord[] }>('/v1/sky/satellites')).satellites;
}
export interface LiveAircraft { callsign: string; country: string; altitudeFeet: number | null; speedKmh: number | null; distanceKm: number; source?: string }
export async function fetchAircraftSnapshot(latitude: number, longitude: number) {
  return request<{ aircraft: LiveAircraft[]; source: string; observed_at: string }>(`/v1/sky/aircraft?lat=${latitude}&lng=${longitude}`);
}
export async function fetchLiveAircraft(latitude: number, longitude: number) {
  const snapshot = await fetchAircraftSnapshot(latitude, longitude);
  return snapshot.aircraft.map((item) => ({ ...item, source: snapshot.source }));
}

// ---------------------------------------------------------------- tonight's sky

/**
 * Tonight's passes, Moon, planets and forecast for a coordinate. Callers must
 * treat a rejection as "omit the section" — the endpoint's own gaps arrive as
 * explicit `*_unavailable` reasons inside a successful response.
 */
export async function fetchTonight(latitude: number, longitude: number, timeoutMs = 12_000) {
  return request<TonightSky>(`/v1/sky/tonight?lat=${latitude}&lng=${longitude}`, {}, timeoutMs);
}

// ---------------------------------------------------------------- news

export async function sendContactMessage(input: { name: string; email: string; topic: string; message: string; website?: string }) {
  return request<{ ok: true }>('/v1/contact', { method: 'POST', body: JSON.stringify(input) });
}

export async function fetchNews(options: { category?: string | null; tag?: string | null; limit?: number; offset?: number } = {}) {
  const query = new URLSearchParams({ limit: String(options.limit ?? 24), offset: String(options.offset ?? 0) });
  if (options.category) query.set('category', options.category);
  if (options.tag) query.set('tag', options.tag);
  return request<NewsList>(`/v1/news?${query}`);
}

export async function fetchNewsPost(slug: string) {
  try { return await request<{ post: NewsPost; related: NewsSummary[] }>(`/v1/news/${encodeURIComponent(slug)}`); }
  catch (error) { if (error instanceof ApiError && error.status === 404) return null; throw error; }
}

// ---------------------------------------------------------------- admin
// The admin key lives in sessionStorage only, so it is gone when the tab closes.

const ADMIN_KEY = 'ttu-admin-key';
export const adminKey = {
  get: () => { try { return window.sessionStorage.getItem(ADMIN_KEY); } catch { return null; } },
  set: (key: string) => { try { window.sessionStorage.setItem(ADMIN_KEY, key); } catch { /* private mode */ } },
  clear: () => { try { window.sessionStorage.removeItem(ADMIN_KEY); } catch { /* private mode */ } },
};

export async function adminRequest<T>(path: string, init: RequestInit = {}, key = adminKey.get()): Promise<T> {
  if (!key) throw new ApiError(401, 'Enter the admin key.');
  const isFormData = init.body instanceof FormData;
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/v1/admin${path}`, {
      ...init,
      headers: { ...(isFormData ? {} : { 'content-type': 'application/json' }), 'x-admin-key': key, ...(init.headers ?? {}) },
    });
  } catch {
    throw new ApiError(null, 'The API could not be reached.');
  }
  const data = await response.json().catch(() => ({})) as { message?: string; error?: string };
  if (!response.ok) throw new ApiError(response.status, data.message ?? data.error ?? `Request failed (${response.status}).`);
  return data as T;
}
